import React, { useState } from 'react'
import Grid from '@mui/material/Grid'

import { getSha256Hash } from '@ecm/data/Hash'
import { formatErrors, HubSpotResponse } from '@ecm/data/HubSpot'
import { Lead } from '@ecm/data/Lead'
import { runClientEff } from '@ecm/effect'
import Button from '@ecm/ui/element/Button'
import { validateEmail } from '@ecm/ui/element/Form/Validation'
import BaseInput from '@ecm/ui/element/input/Input'

export type GenericCtaFormProps = {
  formId: string
  leadProps?: unknown
  small?: boolean
  source?: string
  submitBtnText?: string
  trackGaEvents?: boolean
}

export const GenericCtaForm = ({
  formId,
  leadProps,
  small,
  source,
  submitBtnText,
  trackGaEvents,
}: GenericCtaFormProps) => {
  const [emailInputValue, setEmailInputValue] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [emailValidationError, setEmailValidationError] = useState<string>('')
  const [emailSuccessMessage, setEmailSuccessMessage] = useState<string>('')

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      handleSubmit()
    }
  }

  const handleInputChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setEmailInputValue(e.currentTarget.value)
  }

  const handleBlur = () => {
    setEmailValidationError(validateEmail(emailInputValue))
  }

  const handleSubmit = () => {
    const emailValidationMessage = validateEmail(emailInputValue)
    if (emailValidationMessage) {
      setEmailValidationError(emailValidationMessage)
      return
    }

    setSubmitting(true)
    runClientEff(async eff => {
      try {
        const lead: Lead = {
          email: emailInputValue,
          lifeCycleStage: 'Lead',
          productFamily: 'ECM',
        }

        Object.assign(lead, leadProps)

        const response: HubSpotResponse = await eff.submitGenericLead(lead, formId, source)

        if (!response.errors) {
          setEmailInputValue('')
          setEmailSuccessMessage('The request has been successfully submitted')
          setSubmitting(false)

          if (trackGaEvents) {
            eff.trackGa({
              cp_customer_email: getSha256Hash(lead.email),
              cp_form_slot: small ? 'Sidebar' : 'Inline Body',
              event: 'newsletterFormSuccess',
            })
          }

          setTimeout(() => setEmailSuccessMessage(''), 2500)
        } else {
          console.error(formatErrors(response.errors))
          setSubmitting(false)
        }
      } catch (error) {
        console.error(error)
        setSubmitting(false)
      }
    })
  }

  return (
    <Grid container justifyContent="space-between" pt="1em">
      <Grid
        alignItems="flex-start"
        display="grid"
        justifyContent="center"
        gap={small ? (emailValidationError ? '1.5em 1em' : '0.5em 1em') : '0 1em'}
        gridTemplateColumns={{ md: small ? '1fr' : '2fr 1fr', sm: '1fr', xs: '1fr' }}
        gridTemplateRows="auto"
        width="100%"
        id={submitBtnText === 'Send it to me!' ? (small ? 'send-right' : 'send-bottom') : ''}
      >
        <BaseInput
          id="email"
          label="Email"
          name="email"
          type="email"
          initialValue={emailInputValue}
          message={emailSuccessMessage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onEnter={handleSubmit}
          error={emailValidationError}
          onBlur={handleBlur}
        />

        <Button submitting={submitting} onClick={handleSubmit} primary margin="0" narrow>
          {submitBtnText || 'Submit'}
        </Button>
      </Grid>
    </Grid>
  )
}

export default GenericCtaForm
